import { useLingui } from "@lingui/react";
import dayjs from "dayjs";

type I18nDate = ReturnType<typeof useLingui>["i18n"]["date"];
type SafeI18nDate = (
  date: Parameters<I18nDate>[0],
  options?: Intl.DateTimeFormatOptions,
) => string;

/**
 * Linguiのi18n.dateメソッドの安全なラッパー。
 * 無効な日付が渡された場合に"Invalid date"を返すことで、エラーを防ぎます。
 *
 * @description
 * - 従来の文字列・数値・Dateはdayjsで検証
 * - Lingui 6のTemporal互換値はLingui自身のformatterへ渡す
 * - 無効な日付（null、undefined、不正な文字列など）は"Invalid date"として表示
 * - 有効な日付は通常通りLinguiの日付フォーマットで表示
 *
 * @example
 * ```tsx
 * const { safeI18nDate } = useSafeI18nDate();
 *
 * // 有効な日付
 * safeI18nDate(new Date()); // "2024年1月15日"
 * safeI18nDate("2024-01-15"); // "2024年1月15日"
 *
 * // 無効な日付
 * safeI18nDate(null); // "Invalid date"
 * safeI18nDate("invalid"); // "Invalid date"
 * ```
 *
 * @note
 * `use`から始まるが、`useLingui`がServer Sideでも使えるので、
 * このhookはServer Componentでも使用可能です。
 * Client Componentではhookのルールに従ってください。
 *
 * @returns {{ safeI18nDate: I18nDate }} 安全なi18n.dateメソッドを含むオブジェクト
 */
export function useSafeI18nDate(): {
  safeI18nDate: SafeI18nDate;
} {
  const { i18n } = useLingui();

  const safeI18nDate: SafeI18nDate = (...args) => {
    const date = args[0];
    try {
      if (date === null || date === undefined) {
        return "Invalid date";
      }

      const dayjsCompatibleDate =
        typeof date === "string" ||
        typeof date === "number" ||
        date instanceof Date;
      if (dayjsCompatibleDate && !dayjs(date).isValid()) {
        return "Invalid date";
      }
      return (i18n.date as (...dateArgs: Parameters<SafeI18nDate>) => string)(
        ...args,
      );
    } catch {
      return "Invalid date";
    }
  };

  return {
    safeI18nDate,
  };
}
